import type {
  WebCallAudioEvent,
  WebCallOutboundEvents,
} from '@kanbu/schema/contracts';
import { arrayBufferToBase64, getWsUrlFromHttpUrl } from '@kanbu/shared';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { AppSettings } from '@/constants/AppSettings';
import { useBoundStore } from '@/store/store';

import { useCallDuration } from './hooks/useCallDuration';
import { useMicrophone } from './hooks/useMicrophone';
import {
  useSoundPlayer,
  type AudioOutputMessage,
} from './hooks/useSoundPlayer';
import { VoiceContext } from './hooks/useVoice';
import type { VoiceError, VoiceStatus } from './voiceTypes';

const noop = () => {};

/**
 * A web call has no phone number to identify the caller by, so without this
 * every call minted a brand new, never-reused `ChatUser` server-side (see
 * `initChat` in `callRoutes.ts`). Persisting one id per chat in this browser
 * and sending it on every call lets the server reuse the same `ChatUser`
 * instead.
 *
 * Only safe here because this tester is behind the admin's own auth — it is
 * NOT a substitute for real caller identity. The public embed (post-MVP plan
 * Phase C) must use the chatbot's signed-JWT identity instead of a raw
 * client-supplied id.
 */
function getOrCreateChatUserId(chatId: string): string {
  const key = `kanbu_voice_chat_user_${chatId}`;
  const existing = localStorage.getItem(key);

  if (existing) {
    return existing;
  }

  const id = crypto.randomUUID();
  localStorage.setItem(key, id);

  return id;
}

/**
 * If the server never sends 'ready' (voice-api down, unreachable, or a
 * connection that hangs instead of refusing outright), the raw websocket
 * can sit open with no error and no close — without this, the UI would show
 * "Connecting…" forever with zero feedback.
 */
const CONNECT_TIMEOUT_MS = 10_000;

export type VoiceProviderProps = PropsWithChildren<{
  chatId: string;
  onError?: (err: VoiceError) => void;
  onAudioReceived?: (audioOutputMessage: AudioOutputMessage) => void;
  onAudioStart?: (clipId: string) => void;
  onAudioEnd?: (clipId: string) => void;
}>;

export function VoiceProvider({
  children,
  chatId,
  ...props
}: VoiceProviderProps) {
  // Call duration tracking
  const {
    timestamp: callDurationTimestamp,
    start: startTimer,
    stop: stopTimer,
  } = useCallDuration();

  // Connection status
  const [status, setStatus] = useState<VoiceStatus>({
    value: 'disconnected',
  });

  // Error handling
  const [error, setError] = useState<VoiceError | null>(null);
  const isError = error !== null;
  const isMicrophoneError = error?.type === 'micError';
  const isSocketError = error?.type === 'socketError';
  const isAudioError = error?.type === 'audioError';

  // Refs for event handlers
  const onError = useRef(props.onError ?? noop);
  onError.current = props.onError ?? noop;

  const onAudioReceived = useRef(props.onAudioReceived ?? noop);
  onAudioReceived.current = props.onAudioReceived ?? noop;

  const onAudioStart = useRef(props.onAudioStart ?? noop);
  onAudioStart.current = props.onAudioStart ?? noop;

  const onAudioEnd = useRef(props.onAudioEnd ?? noop);
  onAudioEnd.current = props.onAudioEnd ?? noop;

  // WebSocket reference
  const socket = useRef<WebSocket | null>(null);
  const attempt = useRef(0);
  const ticketRequest = useRef<AbortController | null>(null);

  // Cleared on 'ready' and on disconnect — see CONNECT_TIMEOUT_MS.
  const connectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeout.current) {
      clearTimeout(connectTimeout.current);
      connectTimeout.current = null;
    }
  }, []);

  // A mid-call server-side Gemini reconnect re-sends 'ready' without the
  // client's own websocket ever closing — guards against restarting the
  // duration timer from 00:00:00 on every such resume.
  const hasBecomeReady = useRef(false);

  // Greeting-latency instrumentation (changelist §17 item 3) — timestamps
  // 'ready' and the first audio chunk of a call so the gap can be compared
  // against the server-side "First Gemini audio-out chunk" log. Cleared on
  // every fresh connect() so a resumed mid-call reconnect doesn't re-log.
  const readyAt = useRef<number | null>(null);
  const hasLoggedFirstAudio = useRef(false);

  // Update error handling function
  const updateError = useCallback((err: VoiceError | null) => {
    setError(err);
    if (err !== null) {
      onError.current?.(err);
    }
  }, []);

  const player = useSoundPlayer({
    onError: message => {
      updateError({ type: 'audioError', message });
    },
    onPlayAudio: (id: string) => {
      onAudioStart.current(id);
    },
    onStopAudio: (id: string) => {
      onAudioEnd.current(id);
    },
  });

  const mic = useMicrophone({
    sampleRate: 48000,
    onAudioCaptured: useCallback(
      async arrayBuffer => {
        try {
          if (socket.current?.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket is not open, skipping audio capture');

            return;
          }

          /**
           * The WAV goes over the wire INTACT (S3.6). This used to slice off a
           * hardcoded 44 bytes, which meant the server's careful RIFF
           * chunk-walker saw no header, returned the buffer untouched, and the
           * fragile constant was what actually ran. It also made the sample
           * rate an unenforced agreement between two packages — the header
           * carries it, so the server can now read it instead of assuming.
           */
          const jsonMessage = JSON.stringify({
            type: 'audio',
            payload: {
              chunk: arrayBufferToBase64(arrayBuffer),
              timestamp: Date.now(),
            },
          } satisfies WebCallAudioEvent);

          // Send JSON message to websocket
          socket.current.send(jsonMessage);
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Unknown error';
          updateError({ type: 'socketError', message });
          console.error('Error sending audio data:', e);
        }
      },
      [updateError],
    ),
    onError: useCallback(
      message => {
        console.error(message);
        updateError({ type: 'micError', message });
      },
      [updateError],
    ),
  });

  // Disconnect function
  const disconnect = useCallback(() => {
    attempt.current += 1;
    ticketRequest.current?.abort();
    ticketRequest.current = null;
    stopTimer();
    clearConnectTimeout();

    // Close WebSocket
    if (socket.current) {
      const closing = socket.current;
      socket.current = null;
      closing.close();
    }

    // Stop audio playback
    player.stopAll();

    // Stop microphone
    mic.stop();

    // Update status
    if (status.value !== 'error') {
      setStatus({ value: 'disconnected' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTimer, clearConnectTimeout, player.stopAll, mic.stop, status.value]);

  // Connect function - establishes websocket and microphone
  const connect = useCallback(async () => {
    if (socket.current || ticketRequest.current) disconnect();
    const generation = ++attempt.current;
    const isCurrent = () => generation === attempt.current;
    const request = new AbortController();
    ticketRequest.current = request;
    updateError(null);
    setStatus({ value: 'connecting' });
    hasBecomeReady.current = false;
    readyAt.current = null;
    hasLoggedFirstAudio.current = false;

    clearConnectTimeout();
    connectTimeout.current = setTimeout(() => {
      if (!isCurrent()) return;
      updateError({
        type: 'socketError',
        message: 'Timed out waiting for the voice service to respond.',
      });
      disconnect();
    }, CONNECT_TIMEOUT_MS);

    try {
      /**
       * A3 — the microphone comes FIRST, before anything that costs money.
       *
       * Opening the socket first meant the server had already created a
       * `ChatUser` and a `Thread` and opened (and started billing) a Gemini
       * session by the time the permission prompt was even shown. If the user
       * then denied it, nothing stopped the call: the agent greeted an input
       * stream that would never carry audio and the organization paid for it
       * until someone hung up. Denial now costs nothing.
       */
      const micStarted = await mic.start();
      if (!isCurrent()) return;

      if (!micStarted) {
        clearConnectTimeout();
        setStatus({
          value: 'error',
          reason: 'Microphone unavailable',
        });

        return;
      }

      /**
       * Authorize the call before opening the socket. A browser cannot set
       * headers on a websocket handshake, so the admin token is spent here on
       * a normal request and only the short-lived ticket reaches the URL.
       *
       * `chatUserId` goes in the ticket rather than the socket query string —
       * as a bare parameter, any caller could claim any identity.
       */
      const chatUserId = getOrCreateChatUserId(chatId);
      const ticketResponse = await fetch(
        `${AppSettings.voiceApiUrl}/calls/chats/${chatId}/web/ticket`,
        {
          method: 'POST',
          signal: request.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${useBoundStore.getState().jwt}`,
          },
          body: JSON.stringify({ chatUserId }),
        },
      );

      if (!isCurrent()) return;
      if (!ticketResponse.ok) {
        throw new Error(
          `Voice service refused to start the call (${ticketResponse.status}).`,
        );
      }

      const { ticket } = (await ticketResponse.json()) as { ticket: string };

      if (!isCurrent()) return;

      // Create WebSocket connection
      const wsUrl = getWsUrlFromHttpUrl(AppSettings.voiceApiUrl);
      const ws = new WebSocket(
        `${wsUrl}/calls/chats/${chatId}/web?ticket=${encodeURIComponent(ticket)}`,
      );

      socket.current = ws;
      const ownsSocket = () => isCurrent() && socket.current === ws;

      // WebSocket message handling
      ws.addEventListener('message', (event: MessageEvent) => {
        if (!ownsSocket()) return;
        try {
          // Process incoming message - expecting JSON format only
          if (typeof event.data !== 'string') {
            console.warn(
              'Received non-string message, expected JSON',
              event.data,
            );

            return;
          }

          const jsonData = JSON.parse(event.data) as WebCallOutboundEvents;

          switch (jsonData.type) {
            case 'audio':
              // Create message object for the audio player
              const message: AudioOutputMessage = {
                id: `audio-${Date.now()}`,
                data: jsonData.payload.chunk,
              };

              // Greeting-latency instrumentation (changelist §17 item 3) —
              // compare against the server's "First Gemini audio-out chunk"
              // log to see how much of the gap is network/decode/queueing
              // on this side vs. Gemini's own generation.
              if (!hasLoggedFirstAudio.current) {
                hasLoggedFirstAudio.current = true;
                console.log(
                  'First audio chunk received from server',
                  readyAt.current
                    ? `${Date.now() - readyAt.current}ms since ready`
                    : '(ready timestamp missing)',
                );
              }

              // Send to player and notify listeners
              player.addToQueue(message);
              onAudioReceived.current(message);
              break;

            case 'clear':
              // Stop the audio player
              player.clearQueue();
              break;

            case 'ready':
              // The agent has finished its Gemini handshake and can now
              // hear and respond to the caller — this is what "connected"
              // should mean, not the raw websocket having opened. A
              // mid-call reconnect re-sends this without a new websocket,
              // so the timer only (re)starts the first time.
              clearConnectTimeout();
              setStatus({ value: 'connected' });
              if (!hasBecomeReady.current) {
                hasBecomeReady.current = true;
                readyAt.current = Date.now();
                startTimer();
              }
              break;

            case 'error':
              // The server is about to end the call over this same socket —
              // record the reason now so it survives the 'close' handler's
              // disconnect() (which does not touch `error`).
              updateError({
                type: 'callError',
                message: jsonData.payload.message,
              });
              break;
          }
        } catch (e) {
          console.error('Error processing JSON message:', e);
        }
      });

      // Handle connection events
      ws.addEventListener('open', () => {
        if (!ownsSocket()) return;
        console.log('WebSocket connection opened');

        // The microphone is already running (A3, above); only the player is
        // left. Actual "connected" status still waits for the server's
        // 'ready' event (Gemini's handshake, not just this raw socket).
        player.initPlayer();
      });

      ws.addEventListener('error', error => {
        if (!ownsSocket()) return;
        console.error('WebSocket error:', error);
        updateError({
          type: 'socketError',
          message: 'WebSocket connection error',
          error: error instanceof Error ? error : new Error('WebSocket error'),
        });
        disconnect();
      });

      ws.addEventListener('close', () => {
        if (!ownsSocket()) return;
        disconnect();
      });
    } catch (e) {
      if (!isCurrent()) return;
      const message = e instanceof Error ? e.message : 'Unknown error';

      // The microphone is live from the top of `connect()` now (A3), so
      // anything that fails after it must hand the device back — otherwise a
      // refused call leaves the browser's recording indicator on.
      mic.stop();
      player.stopAll();
      clearConnectTimeout();
      updateError({ type: 'socketError', message });
      setStatus({ value: 'error', reason: message });
    }
  }, [
    updateError,
    chatId,
    player,
    startTimer,
    mic,
    disconnect,
    clearConnectTimeout,
  ]);

  useEffect(
    () => () => {
      attempt.current += 1;
      ticketRequest.current?.abort();
      clearConnectTimeout();
      const closing = socket.current;
      socket.current = null;
      closing?.close();
    },
    [clearConnectTimeout],
  );

  // Create context value
  const ctx = useMemo(
    () => ({
      connect,
      disconnect,
      isMuted: mic.isMuted,
      isAudioMuted: player.isAudioMuted,
      isPlaying: player.isPlaying,
      mute: mic.mute,
      muteAudio: player.muteAudio,
      unmute: mic.unmute,
      unmuteAudio: player.unmuteAudio,
      status,
      error,
      isAudioError,
      isError,
      isMicrophoneError,
      isSocketError,
      callDurationTimestamp,
      playerQueueLength: player.queueLength,
    }),
    [
      connect,
      disconnect,
      player.isAudioMuted,
      player.isPlaying,
      player.muteAudio,
      player.unmuteAudio,
      player.queueLength,
      mic.isMuted,
      mic.mute,
      mic.unmute,
      status,
      error,
      isAudioError,
      isError,
      isMicrophoneError,
      isSocketError,
      callDurationTimestamp,
    ],
  );

  return <VoiceContext.Provider value={ctx}>{children}</VoiceContext.Provider>;
}
